import { z } from 'zod'
import { oc } from '@orpc/contract'

// Minimal, render-ready notification shape (no FHIR at the boundary).
const taskNotificationSchema = z.object({
  id: z.string(),
  taskTitle: z.string(),
  boardId: z.string(),
  boardName: z.string(),
  itemId: z.string(),
  // 'assignment' (a task assigned to you) or 'comment' (a new comment on a task
  // you're assigned to) — drives the wording in the bell.
  kind: z.enum(['assignment', 'comment']),
  assignedByName: z.string(),
  assignedAt: z.string(),
  read: z.boolean(),
})

const patientReadyNotificationSchema = z.object({
  id: z.string(),
  kind: z.literal('patient-ready'),
  patientName: z.string(),
  patientId: z.string(),
  appointmentTime: z.string(),
  encounterId: z.string(),
  appointmentId: z.string(),
  assignedAt: z.string(),
  read: z.boolean(),
})

// Letter signatures (PD-1407): a coordinator asked a provider to sign, or the
// provider signed. Deep-links to the patient chart.
const letterNotificationSchema = z.object({
  id: z.string(),
  kind: z.enum(['letter-signature-requested', 'letter-signed']),
  letterTitle: z.string(),
  patientId: z.string(),
  patientName: z.string(),
  requestId: z.string(),
  actorName: z.string(),
  assignedAt: z.string(),
  read: z.boolean(),
})

const notificationSchema = z.union([
  taskNotificationSchema,
  patientReadyNotificationSchema,
  letterNotificationSchema,
])

// A prescriber's standing DoseSpot notification counts — a *state*, not an event, which is
// why it rides alongside the feed rather than inside it: DoseSpot gives a count that clears
// when the prescriber acts, so it has no id, no read flag and nothing to dismiss.
// Absent for everyone who is not a mapped prescriber, and for a source that could not be
// read — the bell renders no row either way (PD-1318 §12.6).
const doseSpotCountsSchema = z.object({
  pendingPrescriptions: z.number().int().nonnegative(),
  transmissionErrors: z.number().int().nonnegative(),
  refillRequests: z.number().int().nonnegative(),
  changeRequests: z.number().int().nonnegative(),
})

export const notificationList = oc
  .route({ method: 'GET', path: '/v2/notifications' })
  .input(z.object({}).optional())
  // `notifications` is the most recent page; `unreadCount` is the unread count
  // within that page (see handler — realistic volumes stay well under the cap).
  .output(
    z.object({
      notifications: z.array(notificationSchema),
      unreadCount: z.number().int().nonnegative(),
      doseSpot: doseSpotCountsSchema.optional(),
    }),
  )

export const notificationMarkRead = oc
  .route({ method: 'POST', path: '/v2/notifications/{id}/read' })
  .input(z.object({ id: z.string() }))
  .output(z.object({ id: z.string() }))

export const notificationDelete = oc
  .route({ method: 'DELETE', path: '/v2/notifications/{id}' })
  .input(z.object({ id: z.string() }))
  .output(z.object({ id: z.string() }))

// Distinct 3-segment path (`read-all`) so it never collides with the 4-segment
// `notifications/{id}/read` route above.
export const notificationMarkAllRead = oc
  .route({ method: 'POST', path: '/v2/notifications/read-all' })
  .input(z.object({}).optional())
  .output(z.object({ count: z.number().int().nonnegative() }))
